import * as React from "react";
import { TooltipHost, Stack, IStackTokens, PanelType, ITag, TagPicker, Label, IBasePickerSuggestionsProps, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { INameValuePairIndex } from "../../../../../interfaces/INameValuePairIndex";
import { DatasourceService } from "../../../../../services/DatasourceService";
import { DatasourceHelper } from "../../../../helpers/dataSourceHelper";
import { DataSourcePanel } from "../../../../inputs/datasourcePanel";
import { IBaseInputProps } from "../../../../../interfaces/advancedSearch/IBaseInputProps";
import advancedSearchStateManager from "../../../../../../../advancedSearch/advancedSearchStateManager";
import { IDatasourceQueryDto } from "../../../../../interfaces/IDatasourceQueryDto";
import { logger } from "../../../../../../../utilities/logger";
import { sortTerms } from "../../../../helpers/tagHelper";

const stackTokens: IStackTokens = {
    childrenGap: 5
};

const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
        return false;
    }
    return tagList.some(compareTag => compareTag.key === tag.key);
};

const getTextFromItem = (item: ITag) => item.name;

export interface IDatasourceDropdownInputProps extends IBaseInputProps {
    itemLimit?: number
}

const DatasourceTextTagPickerInput: React.FunctionComponent<IDatasourceDropdownInputProps> = props => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const [dataSourceItem, setDataSource] = React.useState(
        DatasourceHelper.getInputDataSource(advancedSearchStateManager.dataSourceItems, props.displayName, props.selectedSites, props.selectedLibraries)
    );
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]); //selected tags
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const cachedSuggestions = React.useRef<ITag[] | undefined>(undefined);

    React.useEffect(() => {
        const setupAsyncWrapper = async () => {
            setIsLoading(true);
            try {
                const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
                if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, props.displayName);
                setBoundedField(boundedFieldName ?? "");
            } catch (error) {
                logger(error, "datasourceTextTagPickerAdvancedSearch.tsx (useEffect 1)");
            }
            setIsLoading(false);
        };

        setupAsyncWrapper();
    }, []);

    const isFirstRun = React.useRef(true);
    React.useEffect(() => {
        try {
            const newSelectedTag: ITag = props.searchInputData?.value
                ? {
                    name: props.searchInputData.value as string,
                    key: props.searchInputData.value as string,
                }
                : null;

            setSelectedTags(newSelectedTag ? [newSelectedTag] : []);

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(newSelectedTag?.key as string ?? "");
        } catch (e) {
            logger(e, "datasourceTextTagPickerAdvancedSearch.tsx (useEffect 2)");
        }
    }, [props.searchInputData?.value]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        const getAllSuggestions: boolean = filterText === "*";
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            try {
                let result: any[];
                if (getAllSuggestions && Array.isArray(cachedSuggestions.current) && cachedSuggestions.current.length > 0) {
                    result = cachedSuggestions.current;
                }
                else {
                    const request: IDatasourceQueryDto = {
                        id: dataSourceItem.id,
                        name: dataSourceItem.name,
                        tenantId: dataSourceItem.tenantId,
                        typeId: dataSourceItem.type.id,
                        typeName: dataSourceItem.type.name,
                        queryParams: !getAllSuggestions
                            ? [{ name: boundedField, value: filterText.trim() }]
                            : null,
                        returnOnlyQueriedProperties: true,
                        allowCache: dataSourceItem.allowCache
                    };
                    result = await dataSourceService.Search(request);
                }
                // const result = await dataSourceService.Search(request);
                if (result.length > 0) {
                    const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                    if (boundFieldProperty) {
                        if (getAllSuggestions && (!Array.isArray(cachedSuggestions.current) || cachedSuggestions.current.length === 0)) {
                            cachedSuggestions.current = result;
                        }

                        suggestedTags = result
                            .map<ITag>(resultRow => {
                                const propertyValue = resultRow[boundFieldProperty]?.trim();
                                return {
                                    name: propertyValue,
                                    key: propertyValue
                                }
                            });
                    }
                }

                // Allow custom entries for searching purposes
                if (suggestedTags.length === 0) {
                    suggestedTags.push({ name: filterText, key: filterText });
                }

                if (suggestedTags.length > 1) {
                    suggestedTags = suggestedTags
                        .filter((tag, index, tags) => {
                            const tagName: string = tag?.name?.toLowerCase();
                            return tagName?.length > 0
                                && (getAllSuggestions || tagName.indexOf(filterText.trim().toLowerCase()) !== -1)
                                && tags.findIndex(t => (t.name === tag.name)) === index
                                && !listContainsTagList(tag, tagList)
                        })
                        .slice(0, 50);
                }

            } catch (error) {
                logger(error, "datasourceTextTagPickerAdvancedSearch.tsx (filterSuggestedTags)");
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const onEmptyResolveSuggestions = async (selectedItems?: ITag[]): Promise<ITag[]> => {
        return await filterSuggestedTags("*", selectedItems);
    }

    const closePanel = () => {
        advancedSearchStateManager.isDataSourcePanelActive = false;
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        props.handleValueChange(props.searchInputData?.index, [newValue]);
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        advancedSearchStateManager.isDataSourcePanelActive = true;
        // updateSearch(props.searchInputData ? props.searchInputData?.value : "");
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        updateSearch(tags?.length === 1 ? tags[0].key as string : "");
        props.handleValueChange(props.searchInputData?.index, tags.map(tag => tag.key).join(', ') as string);
    };

    // const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    //     suggestionsHeaderText: languageStrings.Suggestions,
    //     noResultsFoundText: languageStrings.NoResultsFound,
    // };

    const input = (
        <>
            <Label>{props.displayName}</Label>
            <TagPicker
                className="xb-sp-input"
                removeButtonAriaLabel="Remove"
                selectedItems={selectedTags}
                onResolveSuggestions={filterSuggestedTags}
                onEmptyResolveSuggestions={onEmptyResolveSuggestions}
                resolveDelay={300}
                getTextFromItem={getTextFromItem}
                // pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={onChangeHandle}
                itemLimit={1}
            />
        </>
    );

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>{input}</Stack.Item>
                <Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>
            </Stack>
            <DataSourcePanel
                type={PanelType.customNear}
                displayName={dataSourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={dataSourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
            />
        </>
    );
};

export default DatasourceTextTagPickerInput;